import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { CardTransactionsSummaryLabel } from "Components/CardTransactions/CardTransactionsSummaryLabel";
import { CardTransactionsSummaryTile } from "Components/CardTransactions/CardTransactionsSummaryTile";
import {
  type CardTransactionsCurrencySum,
  type CardTransactionsSumResponse,
  CurrencyCode,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency, getCurrencySymbol } from "Utils/CurrencyUtils";

const RESOURCES = Resources.Components.CardTransactions.Summary;

type Props = {
  sum?: CardTransactionsSumResponse;
  isLoading: boolean;
};

// Loading placeholder only: a single skeleton block. The currency here is never shown (the symbol renders only
// once loaded), so it does not leak a market assumption — unlike a zeroed CZK block, which would misrepresent a
// EUR (Slovak) merchant's totals. When there is genuinely no data we render nothing instead (see below).
const LOADING_PLACEHOLDER: CardTransactionsCurrencySum = {
  currency: CurrencyCode.CZK,
  salesAmount: 0,
  salesCount: 0,
  refundAmount: 0,
  refundCount: 0,
  netAmount: 0,
};

export const CardTransactionsSummaryBox = ({ sum, isLoading }: Props) => {
  const currencies = sum?.currencies ?? [];

  // No data and not loading: don't render a misleading zeroed block in a guessed currency — the empty state lives
  // in the transaction list below.
  if (!isLoading && currencies.length === 0) {
    return null;
  }

  const displayCurrencies =
    currencies.length > 0 ? currencies : [LOADING_PLACEHOLDER];

  return (
    <Paper sx={{ borderRadius: 2.5 }}>
      <Stack gap={4} mt={[4, 6]} ml={[4, 6]} mr={[3, 4]} mb={[3, 4]}>
        {displayCurrencies.map((currencySummary) => (
          <CurrencySummary
            key={currencySummary.currency}
            summary={currencySummary}
            isLoading={isLoading}
          />
        ))}
      </Stack>
    </Paper>
  );
};

type CurrencySummaryProps = {
  summary: CardTransactionsCurrencySum;
  isLoading: boolean;
};

const CurrencySummary = ({ summary, isLoading }: CurrencySummaryProps) => {
  const { t } = useResource();
  const currencyCode = summary.currency;

  const netAmount = formatCurrency({
    amount: summary.netAmount,
    currencyCode,
    minDecimalPlaces: 2,
    isNumberOnly: true,
  });

  const salesAmount = formatCurrency({
    amount: summary.salesAmount,
    currencyCode,
    minDecimalPlaces: 2,
  });

  const refundAmount = formatCurrency({
    amount: summary.refundAmount,
    currencyCode,
    minDecimalPlaces: 2,
  });

  const currency = getCurrencySymbol(currencyCode);

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <CardTransactionsSummaryLabel fontSize={16}>
          {t(RESOURCES.NetAmount)}
        </CardTransactionsSummaryLabel>
        {isLoading ? (
          <Skeleton variant="text" width={230} height={48} />
        ) : (
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="h2" fontWeight={500} color="primary">
              {netAmount}
            </Typography>
            <Typography variant="h5" fontWeight={400} color="primary">
              {currency}
            </Typography>
          </Box>
        )}
      </Stack>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        gap={3}
      >
        <CardTransactionsSummaryTile
          title={t(RESOURCES.Sales)}
          amount={salesAmount}
          countText={t(RESOURCES.SalesCount, { count: summary.salesCount })}
          isLoading={isLoading}
        />
        <CardTransactionsSummaryTile
          title={t(RESOURCES.Refunds)}
          amount={refundAmount}
          countText={t(RESOURCES.RefundsCount, { count: summary.refundCount })}
          isLoading={isLoading}
        />
      </Box>
    </Stack>
  );
};
